/**
 * Costo y margen de la receta de un producto (RES-18).
 *
 * El costo de cada línea usa el costo ACTUAL del insumo multiplicado por la cantidad de la
 * receta convertida a la unidad del insumo. Misma fórmula que el backend (RES-30): primero el
 * `avgCost` del local (promedio móvil) y, si no hay, el `cost`/`refCost` de referencia del
 * catálogo. Si el insumo no está disponible (borrado o no cargado todavía) se usa el `cost`
 * guardado en la línea como respaldo, y si tampoco existe, 0.
 *
 * El backend ya devuelve `recipeCost` y `margin` por producto; esto es para recalcular en vivo
 * mientras se edita la receta, antes de guardar.
 */
import { convertQuantity } from './units';

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Costo de una línea de receta.
 * @param {{inventoryItemId: string, quantity: number|string, unit?: string, cost?: number}} line
 * @param {Array<{itemId: string, unit: string, cost?: number, avgCost?: number}>} inventoryItems
 * @returns {number} costo en moneda (2 decimales), 0 si no se puede calcular
 */
export const recipeLineCost = (line, inventoryItems = []) => {
  if (!line) return 0;
  const item = (inventoryItems || []).find((i) => i.itemId === line.inventoryItemId);
  const avgCost = Number(item?.avgCost);
  const unitCost = Number.isFinite(avgCost) && avgCost > 0 ? avgCost : Number(item?.cost);
  if (item && Number.isFinite(unitCost) && unitCost > 0) {
    const qty = convertQuantity(line.quantity, line.unit || item.unit, item.unit);
    if (qty !== null) return round2(qty * unitCost);
  }
  const fallback = Number(line.cost);
  return Number.isFinite(fallback) && fallback > 0 ? round2(fallback) : 0;
};

/**
 * Costo total de la receta.
 * @param {Array} recipe
 * @param {Array} inventoryItems
 * @returns {number}
 */
export const recipeTotalCost = (recipe, inventoryItems = []) =>
  round2(
    (Array.isArray(recipe) ? recipe : []).reduce(
      (sum, line) => sum + recipeLineCost(line, inventoryItems),
      0
    )
  );

/**
 * Margen bruto contra el precio de venta.
 * @param {number|string} price - precio de venta
 * @param {number} cost - costo de la receta
 * @returns {{amount: number, percent: number}|null} null si no hay precio válido
 */
export const recipeMargin = (price, cost) => {
  const p = Number(price);
  if (!Number.isFinite(p) || p <= 0) return null;
  const amount = round2(p - (Number(cost) || 0));
  return { amount, percent: Math.round((amount / p) * 1000) / 10 };
};
